import React, { useState, useEffect } from 'react';
import {
  Bars3Icon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { Category } from '../../services/categoryService';
import { tourService } from '../../services/tourService';
import { Tour } from '../../types';

const FeaturedTourOrder: React.FC = () => {
  const { featuredCategories, setFeaturedCategories, reorderCategories, loading } = useCategoryOrder();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState<number | null>(null);
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryTours, setCategoryTours] = useState<Record<string, Tour[]>>({});
  const [draggedTourIndex, setDraggedTourIndex] = useState<{ categoryId: string; index: number } | null>(null);
  const [isTourDragOver, setIsTourDragOver] = useState<{ categoryId: string; index: number } | null>(null);

  // Load cover images and tours for each category
  useEffect(() => {
    const loadCategoryData = async () => {
      if (featuredCategories.length === 0) return;

      const images: Record<string, string> = {};
      const tours: Record<string, Tour[]> = {};

      try {
        console.log('🔄 FeaturedTourOrder: Force refreshing tours...');
        // Force refresh to get latest tours from backend
        const allTours = await tourService.getAllTours(true);
        console.log(`✅ FeaturedTourOrder: Loaded ${allTours.length} tours`);

        // Load images and tours for each category
        featuredCategories.forEach((category) => {
          const categoryToursList = allTours.filter(tour => {
            const tourCatId = (tour as any).categoryId || (tour as any).category_id;
            const matchesId = tourCatId === category.id;
            const matchesName = tour.category && tour.category.toLowerCase() === category.name.toLowerCase();
            return matchesId || matchesName;
          });

          if (categoryToursList.length > 0) {
            // Load and order tours for this category
            // Sort tours primarily by the new sortOrder field
            let orderedTours = [...categoryToursList].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

            tours[category.id] = orderedTours;

            // Set cover image from the FIRST tour in the ORDERED list
            if (orderedTours.length > 0 && orderedTours[0].images && orderedTours[0].images.length > 0) {
              images[category.id] = orderedTours[0].images[0];
            }
          } else {
            tours[category.id] = [];
          }
        });

        setCategoryImages(images);
        setCategoryTours(tours);
      } catch (error) {
        console.error('Error loading category data:', error);
      }
    };

    loadCategoryData();
  }, [featuredCategories]);

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Get category image - only from tours, no hardcoded fallbacks
  const getCategoryImage = (category: Category): string => {
    // Only use tour images - return empty string if no tour image available
    if (categoryImages[category.id]) {
      return categoryImages[category.id];
    }

    // Return empty string if no tour image - component should handle this gracefully
    return '';
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);

    // Add visual feedback
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setIsDragOver(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(index);
  };

  const handleDragLeave = () => {
    setIsDragOver(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      reorderCategories(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setIsDragOver(null);
  };

  // Move category up in order
  const moveUp = (index: number) => {
    if (index > 0) {
      reorderCategories(index, index - 1);
    }
  };

  // Move category down in order
  const moveDown = (index: number) => {
    if (index < featuredCategories.length - 1) {
      reorderCategories(index, index + 1);
    }
  };

  // Reorder tours within a category
  const reorderToursInCategory = (categoryId: string, fromIndex: number, toIndex: number) => {
    const tours = categoryTours[categoryId] || [];
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= tours.length || toIndex >= tours.length) {
      return;
    }

    const newTours = [...tours];
    const [movedTour] = newTours.splice(fromIndex, 1);
    newTours.splice(toIndex, 0, movedTour);

    setCategoryTours(prev => ({
      ...prev,
      [categoryId]: newTours
    }));

    // Update category cover image to the first tour's image
    if (newTours.length > 0 && newTours[0].images && newTours[0].images.length > 0) {
      setCategoryImages(prev => ({
        ...prev,
        [categoryId]: newTours[0].images[0]
      }));
      console.log(`📸 Updated category ${categoryId} cover image to first tour: ${newTours[0].title}`);
    }

    // Save order to backend
    try {
      Promise.all(newTours.map((tour, index) =>
        tourService.updateTour(tour.id, { sortOrder: index })
      ))
        .then(() => {
          console.log('✅ All tour orders saved to backend');
          // Clear cache after successful save to ensure fresh data on refresh
          tourService.clearCache();
          console.log('🗑️ Tour cache cleared after reordering');
        })
        .catch(err => console.error('Error saving tour order to backend:', err));
    } catch (error) {
      console.error('Error initiating tour order save:', error);
    }
  };

  // Move tour up in category
  const moveTourUp = (categoryId: string, index: number) => {
    if (index > 0) {
      reorderToursInCategory(categoryId, index, index - 1);
    }
  };

  // Move tour down in category
  const moveTourDown = (categoryId: string, index: number) => {
    const tours = categoryTours[categoryId] || [];
    if (index < tours.length - 1) {
      reorderToursInCategory(categoryId, index, index + 1);
    }
  };

  // Tour drag and drop handlers
  const handleTourDragStart = (e: React.DragEvent, categoryId: string, index: number) => {
    setDraggedTourIndex({ categoryId, index });
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleTourDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedTourIndex(null);
    setIsTourDragOver(null);
  };

  const handleTourDragOver = (e: React.DragEvent, categoryId: string, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsTourDragOver({ categoryId, index });
  };

  const handleTourDragLeave = () => {
    setIsTourDragOver(null);
  };

  const handleTourDrop = (e: React.DragEvent, categoryId: string, dropIndex: number) => {
    e.preventDefault();
    if (draggedTourIndex && draggedTourIndex.categoryId === categoryId && draggedTourIndex.index !== dropIndex) {
      reorderToursInCategory(categoryId, draggedTourIndex.index, dropIndex);
    }
    setDraggedTourIndex(null);
    setIsTourDragOver(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-800"></div>
        <span className="ml-3 text-neutral-600">Loading featured categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-primary-900 mb-2">
              Featured Categories Order
            </h3>
            <p className="text-sm text-neutral-600">
              Drag and drop categories to reorder them. The first category will appear first in the homepage slideshow and featured section.
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-neutral-500">
            <EyeIcon className="h-4 w-4" />
            <span>{featuredCategories.length} categories</span>
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-3">
        {featuredCategories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
            <div className="text-neutral-500 mb-4">
              <Bars3Icon className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
              <p className="text-lg font-medium">No categories available</p>
              <p className="text-sm">Add some categories first to manage their order.</p>
            </div>
          </div>
        ) : (
          featuredCategories.map((category, index) => (
            <div
              key={category.id}
              className={`bg-white rounded-lg shadow-sm border border-neutral-200 transition-all duration-200 ${draggedIndex === index ? 'opacity-50 scale-105 shadow-lg' : ''
                } ${isDragOver === index ? 'border-primary-500 bg-primary-50' : ''
                }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="p-4">
                <div className="flex items-center space-x-4">
                  {/* Drag Handle */}
                  <div className="flex-shrink-0">
                    <div className="bg-neutral-100 hover:bg-neutral-200 rounded p-2 cursor-move transition-colors">
                      <Bars3Icon className="h-5 w-5 text-neutral-600" />
                    </div>
                  </div>

                  {/* Category Cover Image */}
                  <div className="flex-shrink-0">
                    {categoryImages[category.id] ? (
                      <img
                        src={categoryImages[category.id]}
                        alt={category.name}
                        className="w-16 h-16 rounded-lg object-cover border-2 border-neutral-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-neutral-200 flex items-center justify-center border-2 border-neutral-300">
                        <span className="text-2xl">{category.icon || '📸'}</span>
                      </div>
                    )}
                  </div>

                  {/* Category Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        Position {index + 1}
                      </span>
                      <span
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: category.color || '#1e3a8a' }}
                      >
                        {category.name}
                      </span>
                    </div>
                    <h4 className="text-lg font-semibold text-primary-900 truncate">
                      {category.name}
                    </h4>
                    <p className="text-sm text-neutral-600 truncate">
                      {category.description || `Explore our curated ${category.name.toLowerCase()} packages.`}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-500">
                      <span className={category.isActive ? 'text-green-600' : 'text-red-600'}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 flex items-center space-x-2">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="p-2 text-neutral-400 hover:text-primary-600 transition-colors"
                      title={expandedCategories.has(category.id) ? 'Collapse' : 'Expand to view tours'}
                    >
                      {expandedCategories.has(category.id) ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-2 text-neutral-400 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === featuredCategories.length - 1}
                      className="p-2 text-neutral-400 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Tours List */}
              {expandedCategories.has(category.id) && categoryTours[category.id] && (
                <div className="border-t border-neutral-200 bg-neutral-50">
                  <div className="p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-neutral-700">
                        Tours in {category.name} ({categoryTours[category.id].length})
                      </h5>
                    </div>
                    <div className="space-y-2">
                      {categoryTours[category.id].length === 0 ? (
                        <p className="text-sm text-neutral-500 text-center py-4">
                          No tours in this category yet.
                        </p>
                      ) : (
                        categoryTours[category.id].map((tour, tourIndex) => (
                          <div
                            key={tour.id}
                            className={`bg-white rounded-md border border-neutral-200 transition-all duration-200 ${draggedTourIndex?.categoryId === category.id && draggedTourIndex.index === tourIndex
                              ? 'opacity-50 scale-105 shadow-lg' : ''
                              } ${isTourDragOver?.categoryId === category.id && isTourDragOver.index === tourIndex
                                ? 'border-primary-500 bg-primary-50' : ''
                              }`}
                            draggable
                            onDragStart={(e) => handleTourDragStart(e, category.id, tourIndex)}
                            onDragEnd={handleTourDragEnd}
                            onDragOver={(e) => handleTourDragOver(e, category.id, tourIndex)}
                            onDragLeave={handleTourDragLeave}
                            onDrop={(e) => handleTourDrop(e, category.id, tourIndex)}
                          >
                            <div className="p-3 flex items-center space-x-3">
                              {/* Drag Handle */}
                              <div className="flex-shrink-0">
                                <div className="bg-neutral-100 hover:bg-neutral-200 rounded p-1.5 cursor-move transition-colors">
                                  <Bars3Icon className="h-4 w-4 text-neutral-600" />
                                </div>
                              </div>

                              {/* Tour Image */}
                              <div className="flex-shrink-0">
                                <img
                                  src={tour.images && tour.images.length > 0 ? tour.images[0] : (getCategoryImage(category) || '')}
                                  alt={tour.title}
                                  className="w-12 h-12 rounded-md object-cover border border-neutral-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>

                              {/* Tour Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
                                    Tour {tourIndex + 1}
                                  </span>
                                </div>
                                <h6 className="text-sm font-semibold text-primary-900 truncate">
                                  {tour.title}
                                </h6>
                                <p className="text-xs text-neutral-600 truncate">
                                  {tour.shortDescription || tour.description?.substring(0, 60) + '...'}
                                </p>
                                <div className="flex items-center space-x-3 mt-1 text-xs text-neutral-500">
                                  <span>{tour.duration}</span>
                                  {tour.price && <span>${tour.price.toLocaleString()}</span>}
                                  {tour.rating > 0 && (
                                    <span className="flex items-center">
                                      <span className="text-yellow-500 mr-1">★</span>
                                      {tour.rating}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Tour Action Buttons */}
                              <div className="flex-shrink-0 flex items-center space-x-1">
                                <button
                                  onClick={() => moveTourUp(category.id, tourIndex)}
                                  disabled={tourIndex === 0}
                                  className="p-1.5 text-neutral-400 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Move tour up"
                                >
                                  <ArrowUpIcon className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => moveTourDown(category.id, tourIndex)}
                                  disabled={tourIndex === categoryTours[category.id].length - 1}
                                  className="p-1.5 text-neutral-400 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Move tour down"
                                >
                                  <ArrowDownIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <CheckIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">How to manage order:</p>
            <ul className="text-blue-800 space-y-1">
              <li>• <strong>Categories:</strong> Drag and drop or use arrow buttons to reorder categories</li>
              <li>• <strong>Tours:</strong> Click the expand icon (→) to view tours in each category</li>
              <li>• <strong>Tours:</strong> Drag and drop or use arrow buttons to reorder tours within categories</li>
              <li>• The first category will appear in the homepage hero section</li>
              <li>• The first 3 categories will appear in the Featured Safaris section</li>
              <li>• Tour order within each category affects how they appear on category pages</li>
              <li>• All changes are automatically saved</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedTourOrder;
